import { Check, Sparkles } from "lucide-react";
import { useChatbot } from "@/context/ChatbotContext";

const recs = [
  "Improve Sleep",
  "Stay Hydrated",
  "Exercise Regularly",
  "Consider a routine health check-up",
];

export default function Demo() {
  const { openChatbot } = useChatbot();

  return (
    <section
      id="demo"
      data-testid="demo-section"
      className="relative py-28 md:py-36 overflow-hidden"
    >
      <div className="absolute inset-0 grid-radial opacity-70 pointer-events-none" />

      <div className="relative max-w-7xl mx-auto px-6 grid lg:grid-cols-2 gap-16 items-center">
        {/* Left copy */}
        <div>
          <div className="text-xs font-semibold uppercase tracking-[0.25em] text-cyan-400 mb-5">
            Live Demo
          </div>
          <h2 className="font-display text-4xl sm:text-5xl font-semibold tracking-[-0.03em] leading-tight">
            See your{" "}
            <span className="text-gradient-cyan">Health Risk Score</span> in
            real-time.
          </h2>
          <p className="mt-6 text-zinc-400 text-lg leading-relaxed max-w-lg">
            Tap “Analyze My Health” to launch the SilentScan AI assistant. It
            asks a few conversational questions about your lifestyle, then uses
            Google Gemini to generate your personalized Health Risk Score and
            7-day diet plan.
          </p>

          <button
            data-testid="analyze-my-health-btn"
            onClick={openChatbot}
            className="mt-10 inline-flex items-center gap-2 px-8 py-4 rounded-full font-medium btn-glow text-white animate-pulse-glow"
          >
            <Sparkles className="w-4 h-4" />
            Analyze My Health
          </button>

          <div className="mt-10 grid grid-cols-2 gap-3 max-w-md">
            {recs.map((r) => (
              <div
                key={r}
                className="glass-card rounded-2xl px-4 py-3 flex items-center gap-3"
              >
                <div className="w-5 h-5 rounded-full bg-cyan-400/20 flex items-center justify-center">
                  <Check className="w-3 h-3 text-cyan-400" />
                </div>
                <span className="text-sm text-zinc-200">{r}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Phone mockup */}
        <div className="flex justify-center relative">
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="w-[400px] h-[400px] rounded-full bg-cyan-500/10 blur-[120px]" />
          </div>

          <div className="relative w-[320px] h-[640px] rounded-[3rem] border-[12px] border-zinc-900 bg-gradient-to-br from-[#0a0f1f] to-[#050810] shadow-[0_40px_120px_rgba(0,229,255,0.3)] overflow-hidden">
            <div className="absolute top-0 left-1/2 -translate-x-1/2 w-28 h-7 bg-black rounded-b-3xl z-10" />
            <div className="p-6 pt-12 h-full flex flex-col">
              <div className="flex items-center justify-between">
                <div className="text-[10px] uppercase tracking-[0.2em] text-cyan-400 font-semibold">
                  Health Risk Score
                </div>
                <div className="text-[10px] text-zinc-500">SilentScan AI</div>
              </div>

              <div className="mt-6 flex-1 flex flex-col items-center justify-start">
                <div className="relative w-48 h-48">
                  <svg
                    className="w-full h-full -rotate-90"
                    viewBox="0 0 100 100"
                  >
                    <circle
                      cx="50"
                      cy="50"
                      r="42"
                      fill="none"
                      stroke="rgba(255,255,255,0.08)"
                      strokeWidth="6"
                    />
                    <circle
                      cx="50"
                      cy="50"
                      r="42"
                      fill="none"
                      stroke="url(#demograd)"
                      strokeWidth="6"
                      strokeLinecap="round"
                      strokeDasharray={`${(82 / 100) * 264} 264`}
                    />
                    <defs>
                      <linearGradient
                        id="demograd"
                        x1="0"
                        y1="0"
                        x2="1"
                        y2="1"
                      >
                        <stop offset="0%" stopColor="#00E5FF" />
                        <stop offset="100%" stopColor="#0B3D91" />
                      </linearGradient>
                    </defs>
                  </svg>
                  <div className="absolute inset-0 flex flex-col items-center justify-center">
                    <div className="font-display text-5xl font-semibold">82</div>
                    <div className="text-[10px] text-zinc-500 uppercase tracking-widest mt-1">
                      Overall Wellness
                    </div>
                  </div>
                </div>

                <div className="mt-6 w-full">
                  <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-semibold mb-2">
                    Lifestyle Recommendations
                  </div>
                  <div className="space-y-2">
                    {recs.map((r) => (
                      <div
                        key={r}
                        className="flex items-center gap-3 py-2 px-3 rounded-xl bg-white/[0.03] border border-white/5"
                      >
                        <div className="w-4 h-4 rounded-full bg-cyan-400/20 flex items-center justify-center">
                          <Check className="w-2.5 h-2.5 text-cyan-400" />
                        </div>
                        <span className="text-xs text-zinc-200">{r}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
